import { PrismicProvider } from "@prismicio/react";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { ConditionalWrapper, ExternalLink, Image, Text, Toast } from "@sundae/ui-toolkit";
import type { IWalletProviderOptions } from "@sundae/wallet-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import request from "graphql-request";
import { FC, ReactNode, lazy, useMemo } from "react";
import { MigrateLiquidityContextProvider } from "./components/Dialogs/MigrateLiquidityDialog/context";
import HotjarProvider from "./components/Hotjar";
import { APP_NAME } from "./constants/page.constants";
import { SUNDAE_GENERAL_TOAST_ID } from "./constants/toast.constants";
import {
  FetchAssetsByIdDocument,
  FetchAssetsByIdQuery,
  FetchPoolByAssetsDocument,
  FetchPoolByAssetsQuery,
} from "./gql/generated/bramble.sdk";
import {
  AssetFragmentFragment,
  GetAdaPriceDocument,
  GetAdaPriceQuery,
} from "./gql/generated/stats2.sdk";
import { transformBrambleAsset } from "./gql/utils/transformers";
import { prismicClient } from "./prismic/prismic.config";
import { AppContextProvider, ProfileAppContextProvider } from "./stores";
import { ControlCenterContextProvider } from "./stores/ControlCenterContext";
import { NewsContextProvider } from "./stores/NewsContext";
import { OrderContextProvider } from "./stores/OrderContext";
import { useWalletStrategyContext } from "./stores/WalletStrategyContext";
import { IAssetMetaData } from "./types/Asset.types";
import {
  getAssetPriceToday,
  getAssetWarning,
  getAssetsPool,
  normalizeAdaAssetIdsForGql,
} from "./utils/assets.utils";
import { featureIsEnabled } from "./utils/features.utils";
import { prismicLinkResolver } from "./utils/prismic.utils";

const WalletProvider = lazy(() =>
  import("@sundae/wallet-react").then((m) => ({ default: m.WalletProvider }))
);

export type TProvidersProps = {
  children: ReactNode;
};

const queryClient = new QueryClient();

const LazyReactQueryDevTools =
  process.env.NODE_ENV === "development"
    ? lazy(() =>
        import("@tanstack/react-query-devtools").then((m) => ({
          default: m.ReactQueryDevtools,
        }))
      )
    : () => null;

const cache: { [key: string]: AssetFragmentFragment[] } = {};

export const Providers: FC<TProvidersProps> = ({ children }) => {
  const { strategy } = useWalletStrategyContext();

  const options = useMemo<IWalletProviderOptions>(
    () => ({
      debug: window.__APP_CONFIG.envName === "local",
      useHandles: true,
      strategy,
      dAppName: APP_NAME,
      metadataResolver: async function (assetIds): Promise<IAssetMetaData[]> {
        try {
          let assets: AssetFragmentFragment[] = [];
          const normalizedAssetIds = normalizeAdaAssetIdsForGql(assetIds); // There are various asset ids for ADA and we're receiving `ada.lovelace` from the SDK. We need to normalize it to `cardano.ada` for the API.

          const cacheKey = normalizedAssetIds.sort().join(",");

          if (cache[cacheKey]) return cache[cacheKey];

          const [assetsByIdQuery, poolsByAssetsQuery, adaPriceQuery] = await Promise.all([
            request<FetchAssetsByIdQuery>(
              window.__APP_CONFIG.apiUrls.bramble,
              FetchAssetsByIdDocument,
              {
                ids: normalizedAssetIds,
              }
            ),
            request<FetchPoolByAssetsQuery>(
              window.__APP_CONFIG.apiUrls.bramble,
              FetchPoolByAssetsDocument,
              {
                assets: normalizedAssetIds,
              }
            ),
            request<GetAdaPriceQuery>(window.__APP_CONFIG.apiUrls.stats2, GetAdaPriceDocument, {
              assets: normalizedAssetIds,
            }),
          ]);

          const assetsById = assetsByIdQuery;
          const poolsByAssets = poolsByAssetsQuery;
          const { adaPrice } = adaPriceQuery;

          const assetsPool = getAssetsPool(assetsById.assets.byIds, poolsByAssets.pools.byAssets);

          assets = assetsById.assets.byIds.map((asset) => {
            const pool = assetsPool[asset.id];
            const priceToday = getAssetPriceToday(adaPrice, pool);
            return { ...transformBrambleAsset(asset), priceToday };
          });

          cache[cacheKey] = assets.map((asset: IAssetMetaData) => {
            return {
              ...asset,
              warning: getAssetWarning(asset),
            };
          });

          return cache[cacheKey];
        } catch (error) {
          throw new Error(`Failed to fetch asset metadata: ${error.message}`);
        }
      },
    }),
    [strategy]
  );

  return (
    <QueryClientProvider client={queryClient}>
      <WalletProvider options={options}>
        <ProfileAppContextProvider>
          <AppContextProvider>
            <HotjarProvider>
              <OrderContextProvider>
                <TooltipProvider delayDuration={150}>
                  <MigrateLiquidityContextProvider>
                    <PrismicProvider
                      client={prismicClient}
                      richTextComponents={{
                        heading1: ({ children }) => (
                          <Text tag="h1" size="xl">
                            {children}
                          </Text>
                        ),

                        heading2: ({ children }) => (
                          <Text tag="h2" size="lg">
                            {children}
                          </Text>
                        ),
                        heading3: ({ children }) => <Text tag="h3">{children}</Text>,
                        heading4: ({ children }) => <Text tag="h4">{children}</Text>,
                        paragraph: ({ children }) => (
                          <Text tag="p" size="sm">
                            {children}
                          </Text>
                        ),
                        hyperlink: ({ node, text }) => {
                          return (
                            <ExternalLink
                              arrow="top-right"
                              size="sm"
                              variant="primary"
                              href={prismicLinkResolver(node.data)}
                            >
                              {text}
                            </ExternalLink>
                          );
                        },
                        image: ({ node }) => (
                          <Image
                            className="my-2"
                            src={node.url}
                            alt={node.alt ?? "Sundae Labs"}
                            width={node.dimensions.width}
                            height={node.dimensions.height}
                          />
                        ),
                      }}
                    >
                      <NewsContextProvider>
                        <ConditionalWrapper
                          condition={featureIsEnabled("controlCenter")}
                          wrapper={(children) => (
                            <ControlCenterContextProvider>{children}</ControlCenterContextProvider>
                          )}
                        >
                          {children}
                          <Toast id={SUNDAE_GENERAL_TOAST_ID} />
                        </ConditionalWrapper>
                      </NewsContextProvider>
                    </PrismicProvider>
                  </MigrateLiquidityContextProvider>
                </TooltipProvider>
                <LazyReactQueryDevTools position="bottom-left" />
              </OrderContextProvider>
            </HotjarProvider>
          </AppContextProvider>
        </ProfileAppContextProvider>
      </WalletProvider>
    </QueryClientProvider>
  );
};

/**
 * These providers are used here to render information/components after the initial page load animation.
 */
export const ProvidersAfterInitialRender: FC<TProvidersProps> = ({ children }) => {
  // return <FirstVisitContextProvider>{children}</FirstVisitContextProvider>; // new UI has been around for quite some time; no need for first visit dialogs at this point until we have new (breaking) features to introduce.
  return <>{children}</>;
};

export default Providers;
